import React, { useState, useEffect, useCallback, useContext } from "react";
import { TranslatorContext } from "../../../../context/Translator";
import { Link, useNavigate } from "react-router-dom";
import { Row, Col, Spinner, Alert, Table, Badge, Form } from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../../../layouts/PageLayout";
import ProductEditModal from "./ProductEditModal"; // Import Edit Modal

export default function ProductIndex() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [products, setProducts] = useState([]);

    // Modal Edit States
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);

    // Dropdown Data States
    const [filterData, setFilterData] = useState({
        lister: [],
        issuer: [],
        checker: [],
        brands: [],
        stores: []
    });

    // Selected Filter States
    const [search, setSearch] = useState("");
    const [selectedIssuer, setSelectedIssuer] = useState("");
    const [selectedLister, setSelectedLister] = useState("");
    const [selectedChecker, setSelectedChecker] = useState("");
    const [selectedBrand, setSelectedBrand] = useState("");
    const [selectedStore, setSelectedStore] = useState("");

    const imgBaseUrl = process.env.REACT_APP_IMG_URL || "http://localhost:8000/storage/";

    const resolveImageUrl = (path) => {
        if (!path) return "";
        if (path.startsWith("http://") || path.startsWith("https://")) {
            return path;
        }
        return `${imgBaseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
    };

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("authToken");
            const headers = { Authorization: `Bearer ${token}` };

            const [productsRes, initialRes] = await Promise.all([
                axios.get(`${process.env.REACT_APP_API_URL}/admin/products`, { headers }),
                axios.get(`${process.env.REACT_APP_API_URL}/admin/initial-data`, { headers }).catch(() => null)
            ]);

            if (initialRes && initialRes.data?.success) {
                setFilterData({
                    lister: initialRes.data.lister || [],
                    issuer: initialRes.data.issuer || [],
                    checker: initialRes.data.checker || [],
                    brands: initialRes.data.brands || [],
                    stores: initialRes.data.stores || []
                });
            }

            const rawData = productsRes.data?.data ?? productsRes.data;
            if (Array.isArray(rawData)) {
                setProducts(rawData);
            } else if (rawData && typeof rawData === "object") {
                setProducts(Array.isArray(rawData.data) ? rawData.data : [rawData]);
            } else {
                setProducts([]);
            }
        } catch (err) {
            console.error("Error fetching data:", err);
            setError(err.response?.data?.message || "Failed to fetch required data.");
            setProducts([]);
        } finally {
            setLoading(false);
        }
    }, [imgBaseUrl]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleEditClick = (product) => {
        setSelectedProduct(product);
        setShowEditModal(true);
    };

    const handleUpdateSuccess = (updatedItem) => {
        setProducts((prev) =>
            prev.map((item) => (item.id === updatedItem.id ? { ...item, ...updatedItem } : item))
        );
    };

    const productList = Array.isArray(products) ? products : [];

    const filteredProducts = productList.filter((p) => {
        if (!p) return false;
        const q = search.toLowerCase();

        const matchesSearch =
            !search ||
            p.title?.toLowerCase().includes(q) ||
            p.product_code?.toLowerCase().includes(q) ||
            p.brand?.name?.toLowerCase().includes(q) ||
            p.category?.name?.toLowerCase().includes(q);

        const matchesIssuer = !selectedIssuer || String(p.issuer_id || p.issuer?.id) === String(selectedIssuer);
        const matchesLister = !selectedLister || String(p.lister_id || p.lister?.id) === String(selectedLister);
        const matchesChecker = !selectedChecker || String(p.checker_id || p.checker?.id) === String(selectedChecker);
        const matchesBrand = !selectedBrand || String(p.brand_id || p.brand?.id) === String(selectedBrand);
        const matchesStore = !selectedStore || String(p.store_id || p.store?.id) === String(selectedStore);

        return matchesSearch && matchesIssuer && matchesLister && matchesChecker && matchesBrand && matchesStore;
    });

    const handleMarketplaceClick = (mpItem) => {
        if (!mpItem) return;

        const mpType = mpItem.type || mpItem.marketplace?.type;
        const ref = mpItem.reference || "";
        const baseUrl = mpItem.marketplace?.base_url || "";

        let targetUrl = "";

        if (mpType === "link") {
            targetUrl = ref.startsWith("http") ? ref : `https://${ref}`;
        } else if (mpType === "id") {
            const cleanBase = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
            const cleanRef = ref.startsWith("/") ? ref.slice(1) : ref;
            targetUrl = `${cleanBase}${cleanRef}`;
        } else {
            targetUrl = ref.startsWith("http") ? ref : `${baseUrl}${ref}`;
        }

        if (targetUrl) {
            window.open(targetUrl, "_blank", "noopener,noreferrer");
        }
    };

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb d-flex justify-content-between align-items-center">
                            <div>
                                <h3 className="mc-breadcrumb-title">
                                    {t("Products")} ({filteredProducts.length})
                                </h3>
                                <ul className="mc-breadcrumb-list">
                                    <li className="mc-breadcrumb-item">
                                        <Link to="#" className="mc-breadcrumb-link">
                                            {t("Home")}
                                        </Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">{t("Products")}</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    <div className="mc-card">
                        {error && (
                            <Alert variant="danger" dismissible onClose={() => setError("")} className="mb-4">
                                {error}
                            </Alert>
                        )}

                        <Row className="g-3 align-items-end mb-4">
                            <Col sm={6} md={4} lg={2}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold small text-uppercase text-muted mb-2">Search</Form.Label>
                                    <Form.Control
                                        type="search"
                                        placeholder="Search Code, Title..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </Form.Group>
                            </Col>

                            <Col sm={6} md={4} lg={2}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold small text-uppercase text-muted mb-2">Issuer</Form.Label>
                                    <Form.Select value={selectedIssuer} onChange={(e) => setSelectedIssuer(e.target.value)}>
                                        <option value="">All Issuers</option>
                                        {filterData.issuer.map((item) => (
                                            <option key={item.id} value={item.id}>{item.name}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col sm={6} md={4} lg={2}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold small text-uppercase text-muted mb-2">Lister</Form.Label>
                                    <Form.Select value={selectedLister} onChange={(e) => setSelectedLister(e.target.value)}>
                                        <option value="">All Listers</option>
                                        {filterData.lister.map((item) => (
                                            <option key={item.id} value={item.id}>{item.name}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col sm={6} md={4} lg={2}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold small text-uppercase text-muted mb-2">Checker</Form.Label>
                                    <Form.Select value={selectedChecker} onChange={(e) => setSelectedChecker(e.target.value)}>
                                        <option value="">All Checkers</option>
                                        {filterData.checker.map((item) => (
                                            <option key={item.id} value={item.id}>{item.name}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col sm={6} md={4} lg={2}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold small text-uppercase text-muted mb-2">Brand</Form.Label>
                                    <Form.Select value={selectedBrand} onChange={(e) => setSelectedBrand(e.target.value)}>
                                        <option value="">All Brands</option>
                                        {filterData.brands.map((item) => (
                                            <option key={item.id} value={item.id}>{item.name}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-2 text-muted">{t("Loading products...")}</p>
                            </div>
                        ) : (
                            <div className="mc-table-responsive">
                                <Table hover className="mc-table align-middle">
                                    <thead className="mc-table-head primary">
                                        <tr>
                                            <th>Product</th>
                                            <th>Code</th>
                                            <th>Marketplaces</th>
                                            <th>Selling Price</th>
                                            <th>Status</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="mc-table-body even">
                                        {filteredProducts.length > 0 ? (
                                            filteredProducts.map((product) => {
                                                const cardImgPath = product.img?.card_img || product.img?.first_img;
                                                return (
                                                    <tr key={product.id}>
                                                        <td>
                                                            <div className="d-flex align-items-center gap-2">
                                                                {cardImgPath && (
                                                                    <img
                                                                        src={resolveImageUrl(cardImgPath)}
                                                                        alt={product.title}
                                                                        style={{
                                                                            width: "45px",
                                                                            height: "45px",
                                                                            objectFit: "cover",
                                                                            borderRadius: "6px"
                                                                        }}
                                                                    />
                                                                )}
                                                                <span className="fw-semibold text-truncate" style={{ maxWidth: "200px" }}>
                                                                    {product.title || "N/A"}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td>{product.product_code || "N/A"}</td>
                                                        <td>
                                                            <div className="d-flex align-items-center gap-1 flex-wrap">
                                                                {product.marketplaces && product.marketplaces.length > 0 ? (
                                                                    product.marketplaces.map((mpItem) => {
                                                                        const logoPath = mpItem.marketplace?.logo;
                                                                        const mpName = mpItem.marketplace?.name || "Marketplace";
                                                                        const resolvedLogo = resolveImageUrl(logoPath);

                                                                        return (
                                                                            <button
                                                                                key={mpItem.id}
                                                                                type="button"
                                                                                onClick={() => handleMarketplaceClick(mpItem)}
                                                                                className="btn btn-outline-primary btn-sm p-1 d-inline-flex align-items-center gap-1"
                                                                                title={`${mpName} (${mpItem.reference})`}
                                                                                style={{ textDecoration: "none", fontSize: "12px" }}
                                                                            >
                                                                                {logoPath ? (
                                                                                    <img
                                                                                        src={resolvedLogo}
                                                                                        alt={mpName}
                                                                                        style={{
                                                                                            width: "20px",
                                                                                            height: "20px",
                                                                                            objectFit: "contain"
                                                                                        }}
                                                                                        onError={(e) => {
                                                                                            e.target.style.display = "none";
                                                                                        }}
                                                                                    />
                                                                                ) : null}
                                                                                <span>{mpName}</span>
                                                                            </button>
                                                                        );
                                                                    })
                                                                ) : (
                                                                    <span className="text-muted small">None</span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="fw-bold">${product.pricing?.sell_price_dollar || product.sell_price_dollar || "0.00"}</td>
                                                        <td>
                                                            <Badge
                                                                bg={product.sold_on ? "success" : "info"}
                                                                className="text-truncate px-2 py-1"
                                                                style={{
                                                                    maxWidth: "120px",
                                                                    display: "inline-block",
                                                                    overflow: "hidden",
                                                                    verticalAlign: "middle"
                                                                }}
                                                                title={product.sold_on ? "Sold" : (product.workflow_status || product.status || "N/A")}
                                                            >
                                                                {product.sold_on
                                                                    ? "Sold"
                                                                    : (product.workflow_status || product.status || "N/A")
                                                                        .toString()
                                                                        .replace(/_/g, " ")
                                                                        .toUpperCase()}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <div className="mc-table-action d-flex align-items-center gap-2">
                                                                <button
                                                                    type="button"
                                                                    className="material-icons edit border-0 bg-transparent text-primary"
                                                                    onClick={() => navigate(`/products/detail`, { state: { product } })}
                                                                    title="View Details"
                                                                >
                                                                    visibility
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="material-icons edit border-0 bg-transparent text-warning"
                                                                    onClick={() => handleEditClick(product)}
                                                                    title="Edit Product"
                                                                >
                                                                    edit
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan={8} className="text-center py-4">
                                                    {t("No products found")}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>

            {/* Render Edit Modal */}
            {selectedProduct && (
                <ProductEditModal
                    show={showEditModal}
                    handleClose={() => {
                        setShowEditModal(false);
                        setSelectedProduct(null);
                    }}
                    product={selectedProduct}
                    onUpdateSuccess={handleUpdateSuccess}
                    filterData={filterData}
                />
            )}
        </PageLayout>
    );
}